import React from 'react';
import { RevShareEntry } from '../../../models/RevShareData';
import { Box, Button, Card, Typography } from '@mui/material';
import { PricebookBundle, IntervalTypeEnum } from '../../../models/PricebookData';
import { PaidByEnum } from '../../../models/LicenseData';
import { formatDecimalOrWholeDollars } from '../../../helpers/utilityFunctions';
import { getIntervalLabel } from '../../../helpers/pricebookUtils';
import { PricebookInheritanceStatus } from '../../../data/rev-share/helpers/revShareSettingsUtils';
import RevShareEntriesTable from './RevShareEntriesTable';
import PricebookRevShareCardHeader from './PricebookRevShareCardHeader';
import InheritanceDropdown from './InheritanceDropdown';

interface PricebookRevShareCardProps {
  pricebook: PricebookBundle;
  revShareEntries: RevShareEntry[];
  inheritanceStatus: PricebookInheritanceStatus;
  parentTierName: string;
  existsOnParentTier: boolean;
  onAddRevShare: (pricebook: PricebookBundle) => void;
  onViewEdit: (entry: RevShareEntry) => void;
  onRemoveRevShareEntry: (entry: RevShareEntry) => void;
  onInherit: (pricebook: PricebookBundle) => void;
  onOverride: (pricebook: PricebookBundle) => void;
}

const PricebookRevShareCard: React.FC<PricebookRevShareCardProps> = ({
  pricebook,
  revShareEntries,
  inheritanceStatus,
  parentTierName,
  existsOnParentTier,
  onAddRevShare,
  onViewEdit,
  onRemoveRevShareEntry,
  onInherit,
  onOverride,
}) => {
  const activePricebookPrice = pricebook.prices.find(p => p.intervalType !== IntervalTypeEnum.None);
  const priceIntervalSuffix = getIntervalLabel(activePricebookPrice);
  const formattedPricebookPrice =
    activePricebookPrice?.price && activePricebookPrice.price > 0
      ? formatDecimalOrWholeDollars(activePricebookPrice.price / 100)
      : null;
  const pricebookDescription =
    pricebook.pricebook.internalAdminDescription ?? 'No description available';
  const isOrgPays = pricebook.pricebook.paidBy === PaidByEnum.Org;
  const hasRevShareEntries = revShareEntries.length > 0;
  const isFeeShareNotAvailable =
    !activePricebookPrice ||
    activePricebookPrice.price <= 0 ||
    activePricebookPrice.intervalType === IntervalTypeEnum.None;

  const isInherited = Boolean(parentTierName) && inheritanceStatus === 'inherited';
  const isCustom = inheritanceStatus === 'custom';
  const showDropdown =
    parentTierName && existsOnParentTier && (isInherited || isCustom) && !isFeeShareNotAvailable;

  return (
    <Card
      variant={isOrgPays ? 'pricebookAlternate' : 'pricebookPrimary'}
      sx={{
        padding: '20px 20.5px',
        ...((!hasRevShareEntries || isFeeShareNotAvailable) && {
          minHeight: '100px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }),
      }}>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems={hasRevShareEntries && !isFeeShareNotAvailable ? 'flex-start' : 'center'}>
        <PricebookRevShareCardHeader
          pricebookName={pricebook.pricebook.pricebookName}
          priceAmount={formattedPricebookPrice}
          intervalSuffix={priceIntervalSuffix}
          description={pricebookDescription}
        />
        <Box display='flex' alignItems='center' gap={1} sx={{ flexShrink: 0, ml: 3 }}>
          {showDropdown && (
            <InheritanceDropdown
              parentTierName={parentTierName}
              isInherited={isInherited}
              isCustom={isCustom}
              onInherit={() => onInherit(pricebook)}
              onOverride={() => onOverride(pricebook)}
            />
          )}
          {isFeeShareNotAvailable ? (
            <Typography variant='p14' color='info.main' sx={{ whiteSpace: 'nowrap' }}>
              fee share not available
            </Typography>
          ) : (
            (!isInherited || !showDropdown) && (
              <Button
                variant='tertiary'
                color='secondary'
                onClick={() => onAddRevShare(pricebook)}
                sx={{
                  whiteSpace: 'nowrap',
                  fontSize: '14px',
                  lineHeight: '2em',
                }}>
                + Add share
              </Button>
            )
          )}
        </Box>
      </Box>

      {hasRevShareEntries && !isFeeShareNotAvailable && (
        <Box mt={1}>
          <RevShareEntriesTable
            entries={revShareEntries}
            isOrgPays={isOrgPays}
            isInherited={isInherited}
            onViewEdit={onViewEdit}
            onRemove={onRemoveRevShareEntry}
          />
        </Box>
      )}
    </Card>
  );
};

export default PricebookRevShareCard;
